'use client';

import { useSubsidiaryFilter } from '@/@subsidiary/context';
import { Button, Chip, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';

const MAX_VISIBLE_CHIPS = 3;

/**
 * What View-as does not reproduce exactly (M4S-5670 §5, D8). Listed in the
 * banner's tooltip so nobody troubleshoots a client's view against these.
 */
const VIEW_AS_APPROXIMATIONS = [
    'Feature flags, the nav shell and Home are evaluated for you, not the client.',
    'Route access, the DANI tile and the trial banner follow your account.',
    'Sigma dashboards use your Sigma team.',
    'Spotlight, the Billing reports tab and the DANI credit banner are yours.',
    'My Work, DANI history, iOOH and slide recents list your own work.',
    'Reel Studio and Builder are not available while viewing as a client.',
    'Opening the newest AI crosstab may land on one of your drafts.',
];

/**
 * Top status banners for staff masquerade and hierarchy filtering.
 *
 * Staff masquerade remains the prominent mask banner, shown on every page
 * (Builder included): `hideOnPaths` applies only to the hierarchy strips.
 * Hierarchy filtering (client users and internals View-as'd into a group) uses
 * the chip strip with Change opening the multi-select drawer.
 */
export function ViewAsBanner({ hideOnPaths = [] }: { hideOnPaths?: string[] }) {
    const pathname = usePathname();
    const {
        isVisible,
        isInternal,
        options,
        hierarchyOptions,
        hasHierarchySwitcher,
        selectedOrgIds,
        setSelected,
        setHierarchyFilter,
        masqueradeOrgId,
        notifyOrgSwitch,
        openOrgDrawer,
        stopViewAs,
        viewAsOrgName,
        viewAsStatus,
    } = useSubsidiaryFilter();

    if (!isVisible) return null;
    const isHiddenPath = hideOnPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`));

    const masqueradeLabel =
        viewAsOrgName ??
        hierarchyOptions.find((o) => o.orgId === masqueradeOrgId)?.orgName ??
        options.find((o) => o.orgId === masqueradeOrgId)?.orgName ??
        'selected client';

    const showMasqueradeBanner = isInternal && masqueradeOrgId != null;

    // Hierarchy users (non-internal): always show org strip when they have 2+ options.
    const showHierarchyOrgBanner = !isInternal && options.length > 1 && !isHiddenPath;

    // Internals View-as into a hierarchy: always show the org-filter strip (chips + Change).
    const showInternalOrgFilterBanner = isInternal && hasHierarchySwitcher && !isHiddenPath;

    if (!showMasqueradeBanner && !showHierarchyOrgBanner && !showInternalOrgFilterBanner) {
        return null;
    }

    return (
        <>
            {showMasqueradeBanner && (
                <div className="border-b border-secondary-200 bg-secondary-50 px-4 py-2">
                    <div
                        className="flex items-center justify-center gap-2 text-small text-secondary-700"
                        data-testid="view-as-banner"
                    >
                        <Icon icon="lucide:venetian-mask" width={16} />
                        <span>
                            Viewing as <strong>{masqueradeLabel}</strong> · actions are recorded
                        </span>
                        <Tooltip
                            placement="bottom"
                            content={
                                <div className="flex max-w-sm flex-col gap-1 p-1 text-tiny">
                                    <span className="font-medium">
                                        Not exactly what the client sees:
                                    </span>
                                    {VIEW_AS_APPROXIMATIONS.map((line) => (
                                        <span key={line}>{line}</span>
                                    ))}
                                </div>
                            }
                        >
                            <span
                                className="inline-flex cursor-help text-secondary-500"
                                aria-label="What View as does not reproduce"
                            >
                                <Icon icon="lucide:info" width={14} />
                            </span>
                        </Tooltip>
                        <Button
                            size="sm"
                            variant="light"
                            color="secondary"
                            isLoading={viewAsStatus === 'ending'}
                            onPress={() => {
                                notifyOrgSwitch();
                                void stopViewAs('clear');
                            }}
                            startContent={<Icon icon="lucide:x" width={14} />}
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            )}

            {showHierarchyOrgBanner && (
                <HierarchyOrgFilterBanner
                    options={options}
                    selectedOrgIds={selectedOrgIds}
                    onViewAll={() => {
                        notifyOrgSwitch();
                        setSelected([]);
                    }}
                    onChange={openOrgDrawer}
                />
            )}

            {showInternalOrgFilterBanner && (
                <HierarchyOrgFilterBanner
                    options={hierarchyOptions}
                    selectedOrgIds={selectedOrgIds}
                    onViewAll={() => {
                        notifyOrgSwitch();
                        setHierarchyFilter([]);
                    }}
                    onChange={openOrgDrawer}
                />
            )}
        </>
    );
}

function HierarchyOrgFilterBanner({
    options,
    selectedOrgIds,
    onViewAll,
    onChange,
}: {
    options: { orgId: number; orgName: string; sfAccountId: string }[];
    selectedOrgIds: number[];
    onViewAll: () => void;
    onChange: () => void;
}) {
    const isViewAll =
        selectedOrgIds.length === 0 ||
        (selectedOrgIds.length === options.length &&
            options.every((o) => selectedOrgIds.includes(o.orgId)));

    const selectedOrgs = useMemo(() => {
        if (isViewAll) return options;
        return options.filter((o) => selectedOrgIds.includes(o.orgId));
    }, [isViewAll, options, selectedOrgIds]);

    const visible = selectedOrgs.slice(0, MAX_VISIBLE_CHIPS);
    const overflow = selectedOrgs.slice(MAX_VISIBLE_CHIPS);

    return (
        <div className="border-b border-secondary-100 bg-secondary-50/60 px-4 py-1.5">
            <div className="flex flex-wrap items-center justify-center gap-1.5 text-tiny text-secondary-700">
                <Icon icon="lucide:building-2" width={14} className="text-secondary-500" />
                <span>{isViewAll ? 'Viewing all' : 'Viewing'}</span>
                {visible.map((org) => (
                    <Chip
                        key={org.orgId}
                        size="sm"
                        variant="flat"
                        color="secondary"
                        className="h-5 max-w-[10rem] text-tiny"
                    >
                        <span className="truncate">{org.orgName}</span>
                    </Chip>
                ))}
                {overflow.length > 0 && (
                    <Tooltip
                        content={
                            <div className="flex max-w-xs flex-col gap-0.5 p-1">
                                {overflow.map((o) => (
                                    <span key={o.orgId}>{o.orgName}</span>
                                ))}
                            </div>
                        }
                    >
                        <Chip size="sm" variant="bordered" className="h-5 text-tiny">
                            +{overflow.length} more
                        </Chip>
                    </Tooltip>
                )}
                <Button
                    size="sm"
                    variant="light"
                    color="secondary"
                    className="h-6 min-w-0 px-2 text-tiny"
                    onPress={onChange}
                >
                    Change
                </Button>
                {!isViewAll && (
                    <Button
                        size="sm"
                        variant="light"
                        color="secondary"
                        className="h-6 min-w-0 px-2 text-tiny"
                        onPress={onViewAll}
                    >
                        View all
                    </Button>
                )}
            </div>
        </div>
    );
}
